import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/**
 * A press that chooses a picture and hands it on — a participant's photo uploaded or replaced
 * (the advisor record page's roadmap, K5).
 *
 * **It checks nothing before the request** (the F1 kit plan's R3, which departs from the
 * roadmap's K5 wording). The picker is narrowed with `accept` to the four types survey-api takes,
 * and whatever is chosen is sent: survey-api answers 400 for an empty file, 413 for one over its
 * limit and 415 for a type it does not take, and the caller says each in its words. A type or size
 * check here would be a second judge of survey-api's rule (CLAUDE.md's rules of the code), and
 * would intercept the real 413 and 415 the photo's spec needs.
 *
 * **Words in, never an error** (R4): `onChoose` resolves to `null` when the upload landed, or to
 * the sentence to say, drawn beside the button. The button is disabled while an upload is out, and
 * keeps its name so a reader is not handed a different control mid-press. The input's value is
 * cleared after each choice, so choosing the same file again — after a refusal, say — still fires.
 */
export function PhotoInput({
  label,
  onChoose,
  disabled,
  describedBy,
  quiet = false
}: {
  /** The button's name — "Upload a photo", "Replace the photo". */
  label: string;
  onChoose: (file: File) => Promise<Nullable<string>>;
  disabled?: boolean;
  /** The id of a sentence describing the button — a `NotPermitted` where it is drawn off. */
  describedBy?: string;
  /** A link-weight button, for a record card where the photo is a detail, not the task (SR-407). */
  quiet?: boolean;
}) {
  const input = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const choose = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setRefusal(null);
    try {
      setRefusal(await onChoose(file));
    } catch (error) {
      // The contract is a sentence or null; a callback that throws broke it, and whether the
      // upload landed is unknown — so the control says that much rather than nothing, and logs it.
      console.error('[PhotoInput] the upload callback threw instead of answering:', error);
      setRefusal('No answer could be read — check whether the photo was saved before trying again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid justify-items-start gap-1 text-sm">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={quiet ? 'link' : 'outline'}
          className={quiet ? 'h-auto p-0' : undefined}
          disabled={disabled || busy}
          aria-describedby={describedBy}
          onClick={() => input.current?.click()}
        >
          {label}
        </Button>
        {busy && <span className="text-muted-foreground">Uploading…</span>}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        hidden
        tabIndex={-1}
        onChange={(event) => void choose(event)}
      />
      {refusal && (
        <p role="alert" className="text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}
